import { createSelector, PayloadAction } from '@reduxjs/toolkit';
import { TFunction } from 'i18next';
import * as React from 'react';
import Draggable, { DraggableEvent } from 'react-draggable';
import { withTranslation, WithTranslation } from 'react-i18next';

import { openMenu } from '../menuUtils';
import { AppActions } from '../redux/actions/AppActions';
import * as chartActions from '../redux/actions/charts';
import * as actions from '../redux/actions/dtale';
import { useAppDispatch, useAppSelector } from '../redux/hooks';
import {
  selectColumnRange,
  selectCtrlCols,
  selectDataId,
  selectHideColumnMenus,
  selectSettings,
} from '../redux/selectors';
import { Popups, PopupType, RangeState } from '../redux/state/AppState';

import * as bu from './backgroundUtils';
import { ignoreMenuClicks } from './column/columnMenuUtils';
import { ColumnDef, DataViewerPropagateState } from './DataViewerState';
import * as gu from './gridUtils';
import { DataViewerMenuHolder } from './menu/DataViewerMenuHolder';
import {
  buildColumnCopyText,
  buildRangeState,
  CopyText,
  isInRowOrColumnRange,
  toggleSelection,
} from './rangeSelectUtils';

export const SORT_CHARS = {
  ASC: String.fromCharCode(9650),
  DESC: String.fromCharCode(9660),
};

const buildMarkup = (
  t: TFunction,
  colCfg: ColumnDef,
  colName: string,
  backgroundMode?: string,
): { headerStyle: React.CSSProperties; colNameMarkup: React.ReactNode; className: string } => {
  let headerStyle: React.CSSProperties = {};
  let className = '';
  let colNameMarkup: React.ReactNode = colName;
  if (backgroundMode === 'dtypes') {
    const dtypeStyle = bu.dtypeHighlighting(colCfg);
    headerStyle = { ...dtypeStyle, ...headerStyle };
    colNameMarkup = <div title={`DType: ${colCfg?.dtype}`}>{colName}</div>;
    className = Object.keys(dtypeStyle).length ? ' background' : '';
  }
  if (backgroundMode === 'missing' && colCfg?.hasMissing) {
    colNameMarkup = <div title={`${t('Missing Values')}: ${colCfg.hasMissing}`}>{`${bu.missingIcon}${colName}`}</div>;
    className = ' background';
  }
  if (backgroundMode === 'outliers' && colCfg?.hasOutliers) {
    colNameMarkup = <div title={`${t('Outliers')}: ${colCfg.hasOutliers}`}>{`${bu.outlierIcon} ${colName}`}</div>;
    className = ' background';
  }
  if (backgroundMode === 'lowVariance' && colCfg?.lowVariance) {
    colNameMarkup = <div title={`${t('Low Variance')}: ${colCfg.lowVariance}`}>{`${bu.flagIcon} ${colName}`}</div>;
    className = ' background';
  }
  if (colName === gu.EXPANDER_CFG.name) {
    colNameMarkup = null;
  }
  return { headerStyle, colNameMarkup, className };
};

const cancelEvents = (e: DraggableEvent, func: () => void): void => {
  e.preventDefault();
  e.stopPropagation();
  func();
};

/** Component properties for Header */
export interface HeaderProps {
  loading: boolean;
  columns: ColumnDef[];
  rowCount: number;
  columnIndex: number;
  style: React.CSSProperties;
  propagateState: DataViewerPropagateState;
  maxRowHeight?: number;
}

const selectResult = createSelector(
  [selectDataId, selectSettings, selectColumnRange, selectCtrlCols, selectHideColumnMenus],
  (dataId, settings, columnRange, ctrlCols, hideColumnMenus) => ({
    dataId,
    settings,
    columnRange,
    ctrlCols,
    hideColumnMenus,
  }),
);

const Header: React.FC<HeaderProps & WithTranslation> = ({
  loading,
  columns,
  rowCount,
  columnIndex,
  style,
  propagateState,
  maxRowHeight,
  t,
}) => {
  const { dataId, settings, columnRange, ctrlCols, hideColumnMenus } = useAppSelector(selectResult);
  const dispatch = useAppDispatch();
  const toggleColumnMenu = (
    colName: string,
    headerRef: HTMLElement,
  ): PayloadAction<{
    colName?: string;
    headerRef?: HTMLElement;
  }> => dispatch(actions.toggleColumnMenu(colName, headerRef));
  const hideColumnMenu = (colName: string): void => dispatch(actions.hideColumnMenu(colName));
  const updateDragResize = (x: number): PayloadAction<number> => dispatch(AppActions.DragResizeAction(x));
  const stopDragResize = (): PayloadAction<void> => dispatch(AppActions.StopResizeAction());
  const openChart = (chartData: Popups): PayloadAction<Popups> => dispatch(chartActions.openChart(chartData));
  const updateRangeState = (state: RangeState): PayloadAction<RangeState> =>
    dispatch(AppActions.SetRangeStateAction({ ...state }));

  const [drag, setDrag] = React.useState(false);
  const [colWidth, setColWidth] = React.useState<number>();
  const headerRef = React.useRef<HTMLDivElement | null>(null);
  const dragRef = React.useRef<HTMLDivElement | null>(null);
  const [colCfg, colName] = React.useMemo(() => {
    const cfg = gu.getCol(columnIndex, columns, settings.backgroundMode);
    return [cfg, cfg?.name ?? ''];
  }, [columns, settings.backgroundMode]);

  const handleMouseOver = (e: React.MouseEvent): void => {
    if (drag) {
      return;
    }
    const rangeExists = columnRange?.start;
    if (e.shiftKey) {
      if (rangeExists) {
        updateRangeState(buildRangeState({ columnRange: { ...columnRange, end: columnIndex } }));
      }
    } else if (rangeExists) {
      updateRangeState(buildRangeState());
    }
  };

  const resizeStart = (e: DraggableEvent): void => {
    setDrag(true);
    setColWidth(colCfg?.width);
    updateDragResize((e as React.MouseEvent).clientX);
  };

  const resizeCol = (e: DraggableEvent, deltaX: number): void => {
    const width = Math.max((colWidth ?? colCfg?.width ?? 0) + deltaX, 10);
    setColWidth(width);
    updateDragResize((e as React.MouseEvent).clientX);
  };

  const resizeStop = (): void => {
    const updatedColumns = columns.map((col) =>
      col.name === colName ? { ...col, width: colWidth, resized: true } : { ...col },
    );
    setDrag(false);
    setColWidth(undefined);
    propagateState({ columns: updatedColumns, triggerResize: true });
    stopDragResize();
  };

  const buildCopyHandler = (): ((e: React.MouseEvent) => void) => {
    const menuHandler = hideColumnMenus
      ? () => undefined
      : openMenu(
          () => toggleColumnMenu(colName, headerRef.current!),
          () => hideColumnMenu(colName),
          headerRef,
          ignoreMenuClicks,
        );

    return (e: React.MouseEvent): void => {
      if (e.shiftKey) {
        if (columnRange) {
          const title = t('Copy Columns to Clipboard?');
          const callback = (copyText: CopyText): PayloadAction<Popups> =>
            openChart({
              ...copyText,
              type: PopupType.COPY_COLUMN_RANGE,
              title,
              size: 'sm',
              visible: true,
            });
          buildColumnCopyText(dataId, columns, columnRange.start, columnIndex, callback);
        } else {
          updateRangeState(
            buildRangeState({
              columnRange: { start: columnIndex, end: columnIndex },
            }),
          );
        }
        return;
      }
      if (e.ctrlKey || e.metaKey) {
        if (ctrlCols) {
          updateRangeState(buildRangeState({ ctrlCols: toggleSelection(ctrlCols, columnIndex) }));
        } else {
          updateRangeState(buildRangeState({ ctrlCols: [columnIndex] }));
        }
        return;
      }
      menuHandler(e);
    };
  };

  if (columnIndex === 0) {
    return <DataViewerMenuHolder loading={loading} style={style} columns={columns} rowCount={rowCount} />;
  }

  const copyHandler = buildCopyHandler();
  const sortDir = (settings.sortInfo?.find(([col, _dir]) => col === colName) || [null, null])[1];
  let headerStyle = { ...style };
  const markupProps = buildMarkup(t, colCfg!, colName, settings.backgroundMode);
  headerStyle = { ...headerStyle, ...markupProps.headerStyle };

  if (colName === gu.EXPANDER_CFG.name) {
    return <div className="headerCell" style={headerStyle} />;
  }

  const rangeClass =
    isInRowOrColumnRange(columnIndex, columnRange) || ctrlCols?.includes(columnIndex) ? ' in-range' : '';

  const classes = ['text-nowrap'];
  const textStyle: React.CSSProperties = { cursor: 'default' };
  if (!drag && colCfg?.resized) {
    classes.push('resized');
  }
  if (settings.verticalHeaders) {
    headerStyle.height = 'inherit';
    textStyle.width = gu.getRowHeight(0, columns, settings.backgroundMode, maxRowHeight, settings.verticalHeaders) - 15;
    textStyle.textAlign = 'left';
    classes.push('rotate-header');
  } else {
    classes.push('w-100');
  }
  return (
    <div
      className={`headerCell ${markupProps.className}${rangeClass}${drag ? ' active-resize' : ''}`}
      style={{ ...headerStyle, ...(drag ? { width: colWidth } : {}) }}
      onMouseOver={handleMouseOver}
      ref={headerRef}
      data-testid="header-cell"
    >
      <div
        className={classes.join(' ')}
        style={textStyle}
        onClick={(e) => {
          if (drag) {
            return;
          }
          copyHandler(e);
        }}
      >
        {sortDir ? SORT_CHARS[sortDir] : ''}
        {markupProps.colNameMarkup}
      </div>
      <Draggable
        axis="x"
        defaultClassName="DragHandle"
        defaultClassNameDragging="DragHandleActive"
        onStart={(e, data) => cancelEvents(e, () => resizeStart(e))}
        onDrag={(e, { deltaX }) => cancelEvents(e, () => resizeCol(e, deltaX))}
        onStop={(e) => cancelEvents(e, resizeStop)}
        position={{ x: 0, y: 0 }}
        nodeRef={dragRef as React.RefObject<HTMLElement>}
      >
        <div className="DragHandleIcon" ref={dragRef}>
          ⋮
        </div>
      </Draggable>
    </div>
  );
};

export default withTranslation('main')(Header);
